import { useMemo } from "react";
import { Button, Card, List, Space, Tag, Tooltip, Typography } from "antd";
import { CheckOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import type { MajorTerm } from "@/api/catalog";
import {
  useMyEnrollments,
  useRegistrationWindow,
  type EnrollmentStatus,
  type MissingPrerequisite,
} from "@/api/enrollment";

const { Text } = Typography;

/** Trạng thái đăng ký (blocked/tiên quyết/giá) của 1 môn — tra cứu từ
 * `GET /me/major-course-eligibility/`, dùng để quyết định có cho thêm giỏ
 * hàng "học vượt" hay không (Sprint 35). */
export interface CourseEligibilityInfo {
  blocked: boolean;
  missingPrerequisites: MissingPrerequisite[];
  effectivePriceCents: number | null;
}

interface MajorTermsCardProps {
  terms: MajorTerm[];
  currentTermId: number | null;
  /** Giỏ hàng dùng CHUNG với `SuggestedCourses` (Sprint 35 — "học vượt") —
   * `StudyPlan.tsx` truyền vào. Không truyền (dùng ở `MajorPublicDetail.tsx`)
   * → không render nút giỏ hàng nào, giữ nguyên hành vi đọc-only cũ. */
  cart?: { selected: string[]; toggle: (slug: string) => void };
  /** Bắt buộc phải có cùng `cart` để biết môn nào bị chặn tiên quyết (không
   * cho thêm giỏ). Thiếu field này cho 1 slug (dữ liệu chưa load kịp) → coi
   * như chưa biết, không chặn liều — nút vẫn hiện, không disable. */
  eligibilityBySlug?: Map<string, CourseEligibilityInfo>;
}

// Mirror `STATUS_META` ở `MyCourses.tsx` (label/color, bỏ icon — không cần
// trong Tag inline nhỏ ở đây).
const STATUS_TAG: Record<EnrollmentStatus, { label: string; color: string }> = {
  enrolled: { label: "Đang học", color: "blue" },
  completed: { label: "Đã hoàn thành", color: "gold" },
  dropped: { label: "Đã bỏ", color: "default" },
  expired: { label: "Hết hạn", color: "red" },
};

/** Card "Khung chương trình" — danh sách Term theo `order`, đánh dấu kỳ hiện
 * tại, kèm luôn danh sách môn của mỗi kỳ (Sếp phản hồi 2026-07-09: trước đây
 * chỉ hiện `course_count`, không thấy tên môn cụ thể). Dùng chung ở
 * `MajorPublicDetail` (deep-link 1 ngành) và `StudyPlan` (hub chọn ngành,
 * Sprint 26). Sếp phản hồi 2026-07-13: mỗi môn phải ghi rõ trạng thái (đang
 * học/đã hoàn thành/chưa đăng ký...) — trước đây danh sách hoàn toàn tĩnh,
 * không biết sinh viên đã học môn nào. */
export function MajorTermsCard({
  terms,
  currentTermId,
  cart,
  eligibilityBySlug,
}: MajorTermsCardProps) {
  const { data: enrollments } = useMyEnrollments();
  const statusBySlug = useMemo(() => {
    const map = new Map<string, EnrollmentStatus>();
    for (const e of enrollments ?? []) map.set(e.course.slug, e.status);
    return map;
  }, [enrollments]);
  // Không tham số → cùng query key với `SuggestedCourses`, React Query tự
  // dedupe (không gọi API 2 lần dù cả 2 component cùng render trên
  // `StudyPlan`). Xem ghi chú đầy đủ ở `SuggestedCourses.tsx`.
  const { data: registrationWindow } = useRegistrationWindow();
  const registrationOpen = registrationWindow?.is_open ?? true;

  return (
    <Card title="Khung chương trình">
      <List
        dataSource={[...terms].sort((a, b) => a.order - b.order)}
        renderItem={(term) => (
          <List.Item key={term.id} style={{ display: "block" }}>
            <Space style={{ marginBottom: 8 }}>
              <Text strong>
                Kỳ {term.order} — {term.name}
              </Text>
              {term.id === currentTermId && <Tag color="processing">Kỳ hiện tại</Tag>}
              <Text type="secondary">{term.course_count} môn học</Text>
            </Space>

            {term.courses.length === 0 ? (
              <Text type="secondary" style={{ display: "block", paddingLeft: 16 }}>
                Chưa có môn học nào gắn vào kỳ này.
              </Text>
            ) : (
              <List
                size="small"
                dataSource={term.courses}
                renderItem={(course) => {
                  const status = statusBySlug.get(course.slug);
                  const statusTag = status ? STATUS_TAG[status] : null;
                  // Đã ENROLLED/COMPLETED → chỉ hiện Tag trạng thái, không cần
                  // nút giỏ hàng nữa. DROPPED vẫn cho thêm giỏ (đăng ký lại).
                  const canAddToCart =
                    !!cart && status !== "enrolled" && status !== "completed";
                  const eligibility = eligibilityBySlug?.get(course.slug);
                  const blocked = eligibility?.blocked ?? false;
                  const inCart = cart?.selected.includes(course.slug) ?? false;
                  // Cho phép BỎ CHỌN dù ngoài đợt (không chặn thao tác trên
                  // môn đã có sẵn trong giỏ), chỉ chặn THÊM mới.
                  const disabledByWindow = !registrationOpen && !inCart;
                  return (
                    <List.Item
                      key={course.id}
                      style={{ paddingLeft: 16 }}
                      actions={
                        canAddToCart
                          ? [
                              <Tooltip
                                key="add-to-cart"
                                title={
                                  blocked
                                    ? `Chưa đủ điều kiện, còn thiếu: ${(eligibility?.missingPrerequisites ?? [])
                                        .map((p) => `${p.code} - ${p.name}`)
                                        .join(", ")}`
                                    : disabledByWindow
                                      ? "Hiện không trong đợt đăng ký môn học nào."
                                      : undefined
                                }
                              >
                                <Button
                                  size="small"
                                  type={inCart ? "default" : "primary"}
                                  icon={inCart ? <CheckOutlined /> : <ShoppingCartOutlined />}
                                  disabled={blocked || disabledByWindow}
                                  aria-label={
                                    inCart
                                      ? `Bỏ chọn ${course.code}`
                                      : `Thêm ${course.code} vào giỏ hàng`
                                  }
                                  onClick={() => cart?.toggle(course.slug)}
                                >
                                  {inCart ? "Đã thêm" : "Thêm vào giỏ hàng"}
                                </Button>
                              </Tooltip>,
                            ]
                          : undefined
                      }
                    >
                      <Space>
                        <Link to={`/courses/${course.slug}`}>
                          {course.code} — {course.name}
                        </Link>
                        <Tag color={course.is_required ? "blue" : "default"}>
                          {course.is_required ? "Bắt buộc" : "Tự chọn"}
                        </Tag>
                        <Tag color={statusTag?.color ?? "default"}>
                          {statusTag?.label ?? "Chưa đăng ký"}
                        </Tag>
                        {canAddToCart && blocked && <Tag color="warning">Chưa đủ điều kiện</Tag>}
                      </Space>
                      <Text type="secondary" style={{ marginLeft: "auto" }}>
                        {course.credits} tín chỉ
                      </Text>
                    </List.Item>
                  );
                }}
              />
            )}
          </List.Item>
        )}
      />
    </Card>
  );
}

export default MajorTermsCard;
